import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import AuthContext from "./AuthContext";
import PortalContext from "./PortalContext";
import envVariables from "../data/envVariables";
import { useAutoRefresh } from "../components/portal/commercialRefresh";
import { countUnassignedDeals, filterChecklistDeals, normalizeDealsResponse } from "../utilities/commercialChecklist";
import { countNewTasks } from "../utilities/commercialTaskList";

import { fetchCommercialTasks } from "../services/commercialTasks";
import { fetchBrokerActions, summariseBrokerActions } from "../services/brokerActions";

// ─────────────────────────────────────────────────────────────────────────────
// Shared data store for the Commercial page (Checklist + Task List tabs).
//
// Both tabs read from here so that:
//   - refreshing from either tab is a GLOBAL refresh (both datasets reload),
//   - auto-refresh runs once for the page and keeps both datasets current,
//   - the tab bar can show the "unassigned" bell counts for the tab you're
//     NOT currently looking at (both counts are always live).
// ─────────────────────────────────────────────────────────────────────────────

const CommercialDataContext = createContext({
    deals: [],
    checklistDeals: [],
    tasks: [],
    brokerActions: [],
    setDeals: () => {},
    setBrokerActions: () => {},
    setTasks: () => {},
    source: "all",
    setSource: () => {},
    loading: true,
    refreshing: false,
    lastUpdated: null,
    refreshAll: () => {},
    autoRefresh: false,
    setAutoRefresh: () => {},
    unassignedDealCount: 0,
    unresolvedFeedbackDealCount: 0,
    newTaskCount: 0,
});

export const CommercialDataProvider = ({ children }) => {
    const authCtx = useContext(AuthContext);
    const portalCtx = useContext(PortalContext);

    const [deals, setDeals] = useState([]);
    const [tasks, setTasks] = useState([]);
    const [brokerActions, setBrokerActions] = useState([]);
    const [error, setError] = useState("");
    const [source, setSource] = useState("all");
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [lastUpdated, setLastUpdated] = useState(null);

    const dealsUrl = useMemo(() => {
        const base = `${envVariables.apiEndpoint}/api/portal`;

        if (source === "Mine") {
            return `${base}/all-available-deals-by-owner?ownerId=${portalCtx.id}`;
        }

        if (source === "all") {
            return `${base}/all-available-deals`;
        }

        return `${base}/all-available-deals?f=${source}`;
    }, [source, portalCtx.id]);

    const fetchIdRef = useRef(0);

    // `silent` refreshes keep the current tables visible (spinner in the refresh
    // button) instead of blanking to the loading overlay.
    const refreshAll = useCallback(
        ({ silent = true } = {}) => {
            const fetchId = ++fetchIdRef.current;
            if (silent) setRefreshing(true);
            else setLoading(true);

            setError("");

            const config = authCtx?.token
                ? { headers: { "Content-type": "application/json", Authorization: `Bearer ${authCtx.token}` } }
                : {};

            return Promise.allSettled([
                axios.get(dealsUrl, config).then((response) => normalizeDealsResponse(response.data)),
                fetchCommercialTasks(authCtx?.token),
                fetchBrokerActions(authCtx?.token),
            ]).then(([dealResult, taskResult, feedbackResult]) => {
                if (fetchId !== fetchIdRef.current) return;
                if (dealResult.status === "fulfilled") setDeals(dealResult.value);
                if (taskResult.status === "fulfilled") setTasks(taskResult.value);
                if (feedbackResult.status === "fulfilled") setBrokerActions(feedbackResult.value);
                const failures = [
                    dealResult.status === "rejected" && "checklist",
                    taskResult.status === "rejected" && "tasks",
                    feedbackResult.status === "rejected" && "feedback",
                ].filter(Boolean);
                setError(
                    failures.length
                        ? `Could not refresh ${failures.join(" and ")}. Please retry. Previously loaded data may be out of date.`
                        : ""
                );
                if (!failures.length) setLastUpdated(new Date());
                setLoading(false);
                setRefreshing(false);
            });
        },
        [authCtx?.token, dealsUrl]
    );

    // Initial load (and whenever the source filter changes) shows the overlay.
    useEffect(() => {
        refreshAll({ silent: false });
    }, [refreshAll]);

    const refreshSilent = useCallback(() => refreshAll({ silent: true }), [refreshAll]);
    const [autoRefresh, setAutoRefresh] = useAutoRefresh(refreshSilent, "commercialAutoRefresh");

    const feedbackByDeal = useMemo(() => summariseBrokerActions(brokerActions), [brokerActions]);
    const checklistDeals = useMemo(
        () =>
            filterChecklistDeals(deals).map((deal) => ({
                ...deal,
                feedbackSummary: feedbackByDeal[String(deal.id)] || null,
            })),
        [deals, feedbackByDeal]
    );
    const unresolvedFeedbackDealCount = useMemo(
        () => checklistDeals.filter((deal) => deal.feedbackSummary?.unresolvedCount > 0).length,
        [checklistDeals]
    );

    // Bell counts — same rule on both tabs: no Commercial Rep assigned yet.
    const unassignedDealCount = useMemo(() => countUnassignedDeals(checklistDeals), [checklistDeals]);
    const newTaskCount = useMemo(() => countNewTasks(tasks), [tasks]);

    const value = useMemo(
        () => ({
            deals,
            checklistDeals,
            tasks,
            brokerActions,
            setDeals,
            setTasks,
            setBrokerActions,
            source,
            setSource,
            error,
            loading,
            refreshing,
            lastUpdated,
            refreshAll: refreshSilent,
            autoRefresh,
            setAutoRefresh,
            unassignedDealCount,
            unresolvedFeedbackDealCount,
            newTaskCount,
        }),
        [
            deals,
            checklistDeals,
            tasks,
            brokerActions,
            source,
            error,
            loading,
            refreshing,
            lastUpdated,
            refreshSilent,
            autoRefresh,
            setAutoRefresh,
            unassignedDealCount,
            unresolvedFeedbackDealCount,
            newTaskCount,
        ]
    );

    return <CommercialDataContext.Provider value={value}>{children}</CommercialDataContext.Provider>;
};

export const useCommercialData = () => useContext(CommercialDataContext);

export default CommercialDataContext;
